<script setup lang="tsx">
import { computed, watch } from 'vue'
import sliderStore from '@/stores/slider'
import MethodsView from '../MethodsView.vue'
import ModuleForm from '../ModuleForm.vue'
import ModuleTabs from '../ModuleTabs.vue'
import { useI18n } from 'vue-i18n'
import { useModule } from '@/composables/useModule'
import AssetsForm from '../AssetsForm.vue'
import http from '@/services/http'
import { moduleRefreshCallback } from '../shared'
import { System } from '@/models/module-enums-generated'
import { hasPermissionOwn } from '@/services/utils'
import { PermAllModuleDelete, PermAllModuleWrite, PermOwnModuleDelete, PermOwnModuleWrite } from '@/models/enums-generated'
import EditDeleteButtons from '@/components/widgets/EditDeleteButtons.vue'
import { Tabs } from '@/types/module'

const props = defineProps<{ metaId: number; name: string; isCustom: boolean; initialTab?: Tabs }>()

const modelActiveTab = defineModel<Tabs>('activeTab', { default: Tabs.Module })

const { t } = useI18n()
const { moduleMeta, moduleForm, methodsForm } = useModule(props.metaId)

const slider = sliderStore()

const onDelete = async (moduleId: number) => {
	if (!confirm(t('misc.confirmDelete'))) return

	await http.delete(`api/module/delete/${moduleId}`)
	moduleRefreshCallback.value?.()
	slider.close()
}

const canEdit = computed(() => hasPermissionOwn(moduleMeta.value.responsible, PermAllModuleWrite, PermOwnModuleWrite))
const canDelete = computed(() => props.isCustom && hasPermissionOwn(moduleMeta.value.responsible, PermAllModuleDelete, PermOwnModuleDelete))

const viewBtns = (
	<EditDeleteButtons
		editId="edit-module"
		deleteId="delete-module"
		canEdit={canEdit}
		onEdit={edit}
		canDelete={canDelete}
		onDelete={remove}
	/>
)

watch(
	[canEdit, canDelete],
	() => {
		slider.setFooter(viewBtns)
	},
	{ immediate: true },
)

watch(
	() => props.initialTab,
	(tab) => {
		if (tab) modelActiveTab.value = tab
	},
	{ immediate: true },
)

async function edit() {
	slider.openEditModule(props.metaId, props.name, props.isCustom, modelActiveTab.value)
}

function remove() {
	onDelete(moduleMeta.value.module.id)
}
</script>

<template>
	<ModuleTabs v-model:override="modelActiveTab">
		<template #module>
			<ModuleForm v-model="moduleForm" mode="view"></ModuleForm>
			<AssetsForm v-if="moduleMeta.module.type === System" :readonly="true" v-model="moduleMeta.assetAssociations"></AssetsForm>
		</template>
		<template #measures>
			<MethodsView v-model="methodsForm" :is-edit="false"></MethodsView>
		</template>
	</ModuleTabs>
</template>
